'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isEditableShortcutTarget } from '@/lib/utils';
import Mousetrap from 'mousetrap';
import { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';

const FALLBACK_JOKES = [
  'Why do programmers prefer dark mode? Because light attracts bugs.',
  "I told my computer I needed a break, and it said 'No problem, I'll go to sleep.'",
  "Why was the JavaScript developer sad? Because he didn't Node how to Express himself.",
  "What's a programmer's favorite hangout place? Foo Bar.",
  "Why do Java developers wear glasses? Because they can't C#.",
];

type DadJokeResponse = {
  id: string;
  joke: string;
  status: number;
};

function getRandomFallbackJoke(): string {
  return FALLBACK_JOKES[Math.floor(Math.random() * FALLBACK_JOKES.length)]!;
}

async function fetchDadJoke(): Promise<string> {
  try {
    const response = await fetch('https://icanhazdadjoke.com/', {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      return getRandomFallbackJoke();
    }

    const data: DadJokeResponse = await response.json();
    return data.joke;
  } catch {
    return getRandomFallbackJoke();
  }
}

export function SecretPunDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [joke, setJoke] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const loadJoke = useCallback(async () => {
    setIsLoading(true);
    const newJoke = await fetchDadJoke();
    setJoke(newJoke);
    setIsLoading(false);
  }, []);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    if (!isLoading) void loadJoke();
  }, [loadJoke, isLoading]);

  // Register the Mousetrap sequence: mod+6 followed by mod+7
  useEffect(() => {
    const handler = (e?: KeyboardEvent) => {
      if (e && isEditableShortcutTarget(e.target)) {
        return false;
      }

      e?.preventDefault();
      handleOpen();
      return false;
    };

    Mousetrap.bind('mod+6 mod+7', handler);
    return () => {
      Mousetrap.unbind('mod+6 mod+7');
    };
  }, [handleOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">You found a secret!</DialogTitle>
          <DialogDescription>
            You unlocked the hidden joke vault. Shh, don&apos;t tell anyone.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/50 my-2 rounded-lg border p-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="text-muted-foreground size-5 animate-spin" />
            </div>
          ) : (
            <p className="text-center text-base leading-relaxed italic">
              &ldquo;{joke}&rdquo;
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            I&apos;m done laughing
          </Button>
          <Button onClick={() => void loadJoke()} disabled={isLoading}>
            <RefreshCw
              className={`size-4 ${isLoading ? 'animate-spin' : ''}`}
            />
            Give me another!
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
